import { Box, Typography } from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import PropTypes from 'prop-types';
import { default as React, Fragment, useState } from "react";
import { AutoSizer } from "react-virtualized";
import { FixedSizeList } from "react-window";
import { PagePlaceholder } from "../page-placeholder/PagePlaceholder";
import { FixMeLater } from "../../types/FixMeLaterType";

interface DataItem {
  key: string,
  id: string,
  value: string
}

interface Props {
  currentSelection?: string[],
  handleSelection: Function,
  data: DataItem[],
  emptyPlaceholder: string,
  notSelectable?: boolean,
  singleSelection?: boolean
}

interface RowProps {
  index: number;
  style: React.CSSProperties;
  data: FixMeLater;
  isScrolling?: boolean;
}

/**
 * Functional component that renders a virtualized list to display data. Allows for selection of data items.
 * @param props Props passed into this comoponent
 */
function SimpleList(props: Props): JSX.Element {

  const [checked, setChecked] = useState<string[]>(props.currentSelection || [])

  /**
   * Toggles a data item as selected or unselected
   * @param id the id of the data item selected for toggle
   */
  function handleToggle(id: string) {
    const currentIndex = checked.indexOf(id);
    const newChecked = props.singleSelection ? [] : [...checked];

    if (currentIndex === -1) {
      newChecked.push(id);
    } else if (!props.singleSelection) {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked)
    props.handleSelection(newChecked);
  }

  /**
   * Row item within the virtualized list of data
   * @param rowProps Row Props passed into component
   */
  function Row(rowProps: RowProps): JSX.Element {

    const { index, style } = rowProps

    // const index = rowProps.index
    const dataItem = props.data[index]
    if (dataItem != null) {
      return (
        <ListItem
          key={dataItem.key}
          role={undefined}
          dense
          button
          onClick={(_: React.MouseEvent<HTMLDivElement>) => handleToggle(dataItem.id)}
          style={style}
        >
          <ListItemIcon>
            {
              props.notSelectable ?
                <Fragment /> :
                <Checkbox
                  edge="start"
                  checked={checked.indexOf(dataItem.id) !== -1}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ "aria-labelledby": dataItem.id }}
                />
            }
          </ListItemIcon>
          <ListItemText id={dataItem.id} primary={dataItem.value} secondary={dataItem.id !== dataItem.value ? dataItem.id : null} />
        </ListItem>
      )
    } else {
      return (
        <Box />
      )
    }
  }

  return (
    <Fragment>
      {props.data.length === 0 ?
        <PagePlaceholder text={props.emptyPlaceholder} /> :
        <AutoSizer>
          {({ height, width }) => (
            <FixedSizeList
              height={height}
              itemCount={props.data.length}
              itemSize={50}
              width={width}
            >
              {Row}
            </FixedSizeList>
          )}
        </AutoSizer>
      }
    </Fragment>
  );
}

SimpleList.propTypes = {
  currentSelection: PropTypes.array,
  handleSelection: PropTypes.func,
  data: PropTypes.array,
  notSelectable: PropTypes.bool,
  emptyPlaceholder: PropTypes.string,
  singleSelection: PropTypes.bool
}

export default SimpleList